import { motion } from 'framer-motion';
import { useRoomStore } from '../../store/room.store.js';
import { useAuthStore } from '../../store/auth.store.js';
import { useAudioStore } from '../../store/audio.store.js';

interface GameHeaderProps {
  roomCode: string;
  gameTimer: number; // in seconds
  gameStatus: string;
  onPauseToggle: () => void;
  onLeave: () => void;
}

export function GameHeader({
  roomCode,
  gameTimer,
  gameStatus,
  onPauseToggle,
  onLeave,
}: GameHeaderProps) {
  const { players } = useRoomStore();
  const { user } = useAuthStore();
  const { soundEnabled, setSoundEnabled, musicEnabled, setMusicEnabled } = useAudioStore();

  const isMuted = !soundEnabled && !musicEnabled;
  const toggleMute = () => {
    const nextMute = !isMuted;
    setSoundEnabled(!nextMute);
    setMusicEnabled(!nextMute);
  };

  const currentPlayer = players.find((p) => p.displayName === user?.displayName);
  const isHost = currentPlayer ? currentPlayer.isHost : false;

  // Format seconds to mm:ss
  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="w-full flex items-center justify-between px-4 py-2.5 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30 select-none"
    >
      {/* 1. Left: Minimal Back/Leave Arrow Button */}
      <button
        onClick={onLeave}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-surface-200 hover:text-white transition-colors cursor-pointer"
        title="Leave Room"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {/* 2. Center: Minimal Room Code and Countdown timer */}
      <div className="flex items-center space-x-3">
        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[8px] font-black font-mono text-amber-400">
          {roomCode}
        </span>
        <span className="text-purple-300 font-mono tracking-wide font-black text-xs">
          ⏱️ {formatTime(gameTimer)}
        </span>
        {isHost && (
          <span className="text-[7px] font-black uppercase text-amber-300 bg-amber-950/40 border border-amber-500/20 px-1 py-0.5 rounded">
            Host
          </span>
        )}
      </div>

      {/* 3. Right: Actions */}
      <div className="flex items-center space-x-2">
        {/* Mute/Unmute Toggle */}
        <button
          onClick={toggleMute}
          title={isMuted ? "Unmute Audio" : "Mute Audio"}
          className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${
            isMuted
              ? 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'
              : 'bg-white/5 border-white/10 text-surface-300 hover:bg-white/10 hover:text-white'
          }`}
        >
          {isMuted ? '🔇' : '🔊'}
        </button>

        {/* Host Pause Control */}
        <button
          onClick={onPauseToggle}
          className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${
            gameStatus === 'paused'
              ? 'bg-amber-500/20 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-white'
              : 'bg-white/5 border-white/10 text-surface-300 hover:bg-white/10 hover:text-white'
          }`}
        >
          {gameStatus === 'paused' ? '▶' : '⏸'}
        </button>
      </div>
    </motion.header>
  );
}
export default GameHeader;
